// The product page templates and what each one needs from a product — the one place it is said. A product's
// profile (API catalog lib/product-profiles) names its template (`page`): the book page with the player
// (ProductPage), the album page (ProductPage, isAlbum) or the store page (StoreProductPage). The editor asks
// for what the template renders, no more:
//
//   required  what the template always renders: the editor always shows it
//   needs     what of it the page cannot do without (a reviewer is told when a product lacks it)
//   optional  what the template renders when the product has it (credits on a store page, a trailer): the
//             product's type switches it on in its profile (`fields`, Admin → Taxonomy → Behaviour)
//
// Beside the template's own: what every product has (its listing card, its category, who sells it), and what
// its kind needs to be sold (a physical product's weight and delivery, a key's platforms, a download's files).
// The product's details (pages, ISBN, duration, age rating, weight…) are its Fields, which its type's field
// presets name — not dedicated fields.

/** The templates and the dedicated fields each renders (StoreProductPage, ProductPage and their parts). */
const PAGE_TEMPLATES = {
  book: {
    required: ["name", "description", "cover", "square", "author", "languages", "pages"],
    needs: ["name", "description", "cover", "square", "author"],
    optional: ["author_display_name", "illustrator", "translator", "narrator", "thumb"],
  },
  album: {
    required: ["name", "description", "cover", "square", "author", "pages"],
    needs: ["name", "cover", "square", "author"],
    optional: ["author_display_name", "narrator", "languages", "thumb"],
  },
  store: {
    // its gallery: the main picture, and more when the product has them
    required: ["name", "description", "square", "screenshots"],
    needs: ["name", "description", "square"],
    optional: ["original", "thumb", "author", "author_display_name", "illustrator", "translator", "narrator", "preview"],
  },
}

// what every product has, whatever its page: its listing card (the square and the second picture shown on
// hover, ProductBox), its category, its place on its author's page, who sells it and whether it is offered,
// and its videos (the gallery shows them; the main one is its trailer)
const COMMON_FIELDS = ["name", "square", "hoverImage", "category", "presentationOrder", "publisher", "availableForPurchase", "videos", "bookTrailer"]

// what a product's kind needs to be sold
const KIND_FIELDS = {
  physical: ["weight", "packagingWeight", "free_shipping", "stock"],
  license: ["platforms", "stock"],
  download: ["deliverables"],
}

// what every product needs to be sold, and what its kind needs on top
const COMMON_NEEDS = ["category", "price"]
const KIND_NEEDS = { physical: ["stock"] }

// a profile's kind (the product's `profile` and a content types list entry both carry it)
const kindOf = (profile) => profile?.kind || ""

/** What a product of this profile cannot be shown or sold without: its template's needs, the common, its kind's. */
export const needsOf = (profile) => [...new Set([...templateOf(profile).needs, ...COMMON_NEEDS, ...(KIND_NEEDS[kindOf(profile)] || [])])]

/** The template a profile (or a content types list entry) names: store when it names none. */
const templateOf = (profile) => PAGE_TEMPLATES[profile?.page] || PAGE_TEMPLATES.store

/** The optional fields of its template a profile has switched on (`fields`, any others ignored). */
const chosenOptional = (profile) => {
  const optional = templateOf(profile).optional
  const chosen = Array.isArray(profile?.fields) ? profile.fields : []
  return chosen.filter((field) => optional.includes(field))
}

/** Every dedicated field a product of this profile uses: its template's, the common ones and its kind's. */
const fieldsOf = (profile) => {
  const kind = kindOf(profile)
  return [...new Set([...COMMON_FIELDS, ...templateOf(profile).required, ...chosenOptional(profile), ...(KIND_FIELDS[kind] || [])])]
}

/** Does a product of this profile use the field? A profile not known yet shows everything (the editor while it loads). */
export const usesField = (profile, field) => (profile ? fieldsOf(profile).includes(field) : true)
